import React, { useState, useEffect, useCallback } from "react";
import Modal from "./Modal";

export interface PromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  placeholder?: string;
  onSubmit: (value: string) => void;
  submitLabel?: string;
  cancelLabel?: string;
}

const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  title = "Enter value",
  message,
  placeholder = "",
  onSubmit,
  submitLabel = "OK",
  cancelLabel = "Cancel",
}) => {
  const [value, setValue] = useState("");

  const resetAndClose = useCallback(() => {
    setValue("");
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) setValue("");
  }, [isOpen]);

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed) {
      onSubmit(trimmed);
      setValue("");
      onClose();
    }
  }, [value, onSubmit, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={title}
      size="small"
      showCloseButton={true}
      footer={
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={resetAndClose}
            style={{
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!value.trim()}
            style={{
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#fff",
              background: value.trim() ? "#6366f1" : "#9ca3af",
              border: "none",
              borderRadius: "8px",
              cursor: value.trim() ? "pointer" : "not-allowed",
            }}
          >
            {submitLabel}
          </button>
        </div>
      }
    >
      <div>
        {message && (
          <p style={{ margin: "0 0 12px", fontSize: "14px", color: "#6b7280" }}>
            {message}
          </p>
        )}
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoFocus
          style={{
            width: "100%",
            padding: "10px 12px",
            fontSize: "14px",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            boxSizing: "border-box",
          }}
        />
      </div>
    </Modal>
  );
};

export default PromptModal;
